<template>
  <div
    class="h-fit bg-white break-inside-avoid border border-gray-100 hover:border-gray-200 hover:shadow-xs hover:bg-white"
  >
    <div class="p-2" v-if="showUserAvatar">
      <ArtistView :artwork="artwork" />
    </div>
    <div>
      <UCarousel
        v-slot="{ item }"
        loop
        :arrows="false"
        :dots="galleryItems.length > 1"
        wheel-gestures
        :prev="{ variant: 'solid' }"
        :next="{ variant: 'solid' }"
        :items="galleryItems"
        :ui="{
          container: 'gap-0 items-center',

          // 1. Show only one item at a time
          controls:
            'absolute bottom-5 sm:left-14 sm:right-14 right-2.5 left-2.5 flex items-center',
          // 3. Make the arrows visible (adding background and positioning)
          prev: 'cursor-pointer left-0 bg-white border border-gray-200 hover:bg-primary hover:text-white hover:border-primary rounded-full size-8 flex items-center justify-center text-primary transition-colors backdrop-blur-sm',
          next: 'cursor-pointer right-0 bg-white border border-gray-200 hover:bg-primary hover:text-white hover:border-primary rounded-full size-8 flex items-center justify-center text-primary transition-colors backdrop-blur-sm',

          // 4. Style the pagination dots
          dots: 'absolute -bottom-2.5 flex justify-center gap-x-3',
          dot: 'size-2 min-w-2 rounded-full bg-white border border-gray-200 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-600 transition-all',
        }"
      >
        <NuxtLink
          class="block w-full"
          :to="$localePath(`/artwork-details/${artwork._id}`)"
        >
          <BaseImage
            @contextmenu.prevent
            @dragstart.prevent
            :uri="item"
            :alt="artwork.title[locale as Locale]"
            img-class="sm:object-cover hover:object-contain transition-all duration-500 object-contain w-full sm:h-96 h-auto cursor-pointer"
            loader-class="w-full min-h-80 flex items-center justify-center"
            icon-class="size-24 min-w-24"
          />
        </NuxtLink>
      </UCarousel>
    </div>
    <div class="flex items-center justify-between p-2">
      <div class="flex items-center space-x-2 cursor-pointer relative w-full">
        <div class="w-fit">
          <ClientOnly>
            <p class="text-sm text-gray-900 font-medium">
              {{ displayName }}
            </p>
          </ClientOnly>
          <div
            v-if="artwork.isForSale && artwork.price"
            class="flex items-center space-x-1 text-sm text-gray-800"
          >
            <Icon name="ph:tag" size="12" />
            <p class="text-gray-500 text-xs">{{ artwork.price }}֏</p>
          </div>
        </div>
      </div>

      <button
        @click="favorite.toggleFavorite(artwork._id)"
        :disabled="favorite.loading"
        :name="
          favorite.favoriteIds.includes(artwork._id)
            ? $t('artwork_details.in_favorites')
            : $t('artwork_details.add_favorite')
        "
        :aria-label="
          favorite.favoriteIds.includes(artwork._id)
            ? $t('artwork_details.in_favorites')
            : $t('artwork_details.add_favorite')
        "
        class="size-10 min-w-10 flex items-center justify-center cursor-pointer"
      >
        <Icon
          name="ph:heart-fill"
          class="text-rose-300"
          size="24"
          v-if="favorite.loading && artwork._id === favorite.currentId"
        />
        <Icon
          name="ph:heart-fill"
          :class="
            favorite.favoriteIds.includes(artwork._id)
              ? 'text-rose-500'
              : 'text-gray-300'
          "
          v-else
          size="24"
        />
      </button>
    </div>
    <slot name="footer"></slot>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { cutName } from "../../utils/helpers";
import { useWindowSize } from "@vueuse/core";
import type { Artwork } from "~~/types/artwork";
import { useI18n } from "vue-i18n";
import type { Locale } from "~~/types/global";

const { locale } = useI18n();

const props = withDefaults(
  defineProps<{
    artwork: Artwork;
    showUserAvatar?: boolean;
  }>(),
  {
    showUserAvatar: false,
  },
);
defineEmits(["preview"]);

const { width } = useWindowSize();
const favorite = useFavoriteStore();

const displayName = computed(() => {
  const currentKey = locale.value as Locale;

  const title = props.artwork.title[currentKey] || props.artwork.title.en || "";

  const limit = width.value > 400 ? 25 : 22;
  return cutName(title, limit);
});

const galleryItems = computed<string[]>(() => {
  const mainImage = props.artwork?.thumbnail;
  const gallery = props.artwork?.gallery || [];
  return mainImage ? [mainImage, ...gallery] : [...gallery];
});
</script>
